import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled, Typography, Button, useMediaQuery, useTheme, NoSsr } from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { CashbackProcessFlow } from '../cashback/CashbackProcessFlow';
import { colors } from '../../plugins/material-ui/themes/onvistaTheme';
import { ButtonProps } from '../../components/layout/buttonColor';
import { buttonStyle, cbWizardStartLink } from '../../src/helpers/buttonHelpers';
import { BlockContainer } from './BlockContainer';

const PigImage = styled('div')(({ theme }) => ({
  display: 'block',
  position: 'relative',
  backgroundImage: "url('/images/features/landingMain/pig.svg')",
  backgroundSize: '215px 215px',
  height: '215px',
  width: '100%',
  top: `${-152 - parseInt(theme.spacing(4))}px`, // theme.spacing(3) is padding of BlockContainer
  left: 0,
  backgroundRepeat: 'no-repeat',
  [theme.breakpoints.down('sm')]: {
    backgroundSize: '110px 110px',
    height: '110px',
    top: `${-65 - parseInt(theme.spacing(3))}px`,
  },
}));

const PigBrightImage = styled(PigImage)({
  backgroundImage: "url('/images/features/landingMain/pig-bright.svg')",
});

const PigWrapper = styled('div')({ height: 0, alignSelf: 'left' });

const Content = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  '& > :not(style)': {
    margin: `0 0 ${theme.spacing(2)}`,
  },

  '& > img:first-of-type': {
    marginLeft: 0,
  },
  '& > img:last-of-type': {
    marginRight: 0,
  },
}));

const TypographyWhite = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
}));

const TitleWhite = styled(TypographyWhite)(({ theme }) => ({
  marginTop: theme.spacing(3),
  zIndex: 1,
}));

const SubtitleWhite = styled(TypographyWhite)({
  maxWidth: 500,
});

const Title = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  zIndex: 1,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  maxWidth: 500,
  paddingBottom: theme.spacing(3),
}));

const ProcessTitle = styled(Typography)(({ theme }) => ({
  paddingBottom: theme.spacing(3),
}));

const ApplyButton = styled(Button)({
  background: colors.sun,
  color: colors.white,
});

const Divider = styled('div')(({ theme }) => ({
  height: theme.spacing(2),
}));

const Description = styled(Typography)({
  maxWidth: '300px',
});

interface Props extends ButtonProps {
  type?: 'type1' | 'type2';
  hasPiggy?: boolean;
  backgroundColor?: string;
}

export const CallToAction = (props: Props) => {
  const { t } = useTranslation();
  const isType2 = props.type === 'type2';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));

  const subTitle = isType2
    ? t('landingMain.callToAction.subTitleType2')
    : t('landingMain.callToAction.subTitleType1');
  return (
    <BlockContainer backgroundColor={props.backgroundColor ?? colors.corp500} spacingBottom={3.5}>
      {props.hasPiggy && (
        <PigWrapper>
          <PigImage />
        </PigWrapper>
      )}
      <Content>
        <TitleWhite variant="h2">{t('landingMain.callToAction.title')}</TitleWhite>
        <SubtitleWhite variant="body1">{subTitle}</SubtitleWhite>
        {isType2 && <Divider />}
        <ApplyButton
          style={buttonStyle(props)}
          variant="primary"
          href={cbWizardStartLink(props.buttonLinkAttributes)}
          size={isType2 ? 'large' : 'medium'}
          endIcon={isType2 ? <ArrowRightAltIcon style={{ fontSize: 40 }} /> : undefined}
        >
          <NoSsr>
            {t(
              isMobile
                ? 'landingMain.callToAction.applyButtonXs'
                : 'landingMain.callToAction.applyButton'
            )}
          </NoSsr>
        </ApplyButton>
      </Content>
    </BlockContainer>
  );
};

export const BigCallToAction = (props: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));

  return (
    <BlockContainer backgroundColor={colors.corp200}>
      {props.hasPiggy && (
        <PigWrapper>
          <PigBrightImage />
        </PigWrapper>
      )}
      <Content data-sectionid="call-to-action">
        <Title variant="h2">{t('cashback.bigCallToAction.title')}</Title>
        <Subtitle variant="body1">{t('cashback.bigCallToAction.subTitleType')}</Subtitle>
        <ProcessTitle variant="h4">{t('cashback.bigCallToAction.processTitle')}</ProcessTitle>
        <CashbackProcessFlow completedSteps={['enterData', 'print', 'send']} />
        <ApplyButton
          style={buttonStyle(props)}
          variant="primary"
          href={cbWizardStartLink(props.buttonLinkAttributes)}
          size="large"
          endIcon={<ArrowRightAltIcon style={{ fontSize: 40 }} />}
          data-eventid="book-cashback-button"
        >
          <NoSsr>
            {t(
              isMobile
                ? 'landingMain.callToAction.applyButtonXs'
                : 'landingMain.callToAction.applyButton'
            )}
          </NoSsr>
        </ApplyButton>
        <Description variant="body2">{t('cashback.bigCallToAction.description')}</Description>
      </Content>
    </BlockContainer>
  );
};
